import * as yup from 'yup';
import { captableInvestmentFormSchema } from '../schemas/CaptableInvestment.schema';
import { StakeholderInvestmentSchemas } from '../schemas/StakeholderInvestment.schema';

export enum CaptableType {
  system = 'system',
  user = 'user',
}

export enum CaptableSource {
  aumni = 'aumni',
  carta = 'carta',
  diligentEquity = 'diligent-equity',
  // FD-2756: system-generated cap table built from Aumni + DealDoc staging data.
  dealDocs = 'deal-docs',
  // Pre-existing backend source for managed-services-imported cap tables; added with FD-2765.
  managedServices = 'managed_services',
  // System-generated cap table built from the client's Snowflake share. Displayed as "Snowflake".
  snowflake = 'snowflake',
  // Legacy value for `snowflake`; remove once every environment runs the renamed API.
  kp = 'kp',
  none = 'none',
}

/** Summary for share class investments (common/preferred) */
export interface IShareClassSummary {
  fullyDilutedShares?: number;
  outstandingShares?: number;
  cashRaised?: number;
  totalLiquidationPreference?: number;
}

/** Summary for option pools */
export interface IOptionPoolSummary {
  authorizedShares?: number;
  fullyDilutedShares?: number;
  outstandingEquityAwardDerivatives?: number;
  outstandingCommittedRestrictedStockAwards?: number;
  grantedOptions?: number;
  ungrantedOptions?: number;
  exercisedOptionPoolsAmount?: number;
}

/** Summary for notes */
export interface INoteSummary {
  cashRaised?: number;
  principal?: number;
  interest?: number;
}

/** Summary for warrants */
export interface IWarrantSummary {
  fullyDilutedShares?: number;
  outstandingWarrants?: number;
  cashRaised?: number;
}

/** Per-share-class liquidation preference, keyed by share-class `name`. */
export interface ILiquidationPreferenceByClass {
  name: string;
  seniority?: number; // lower = more senior
  liquidationMultiplier?: number; // e.g. 1, 1.5, 2
}

export interface ICaptableInvestmentSummary {
  cashSummary: {
    cashRaised?: number;
  };
  debtSummary: {
    cashRaised?: number;
  };
  commonInvestmentsSummary?: IShareClassSummary;
  preferredInvestmentsSummary?: IShareClassSummary;
  noteBlocksSummary?: INoteSummary;
  optionPoolsSummary?: IOptionPoolSummary;
  warrantBlocksSummary?: IWarrantSummary;
  liquidationPreferenceStack?: ILiquidationPreferenceByClass[];
}

export interface ICaptableFundSummary {
  fundId?: number; // optional - nullable in API
  fundName: string;
  stakeholderName?: string;
  cashSummary: {
    cashRaised?: number;
  };
  debtSummary: {
    cashRaised?: number;
  };
  commonInvestmentsSummary?: IShareClassSummary;
  preferredInvestmentsSummary?: IShareClassSummary;
  noteBlocksSummary?: INoteSummary;
  optionPoolsSummary?: IOptionPoolSummary;
  warrantBlocksSummary?: IWarrantSummary;
}

/** Detail for a single investment within a stakeholder summary */
export interface IInvestmentDetail {
  name: string;
  investmentType: string;
  fullyDilutedShares?: number;
  outstandingShares?: number;
  cashRaised?: number;
  outstandingEquityAwardDerivatives?: number;
  outstandingCommittedRestrictedStockAwards?: number;
  outstandingWarrants?: number;
  interest?: number;
  principal?: number;
}

/** Summary for a stakeholder across all investment types */
export interface IStakeholderSummary {
  name: string;
  isClient: boolean;
  commonInvestmentsSummary?: IShareClassSummary;
  preferredInvestmentsSummary?: IShareClassSummary;
  cashSummary?: { cashRaised?: number };
  debtSummary?: { cashRaised?: number };
  noteBlocksSummary?: INoteSummary;
  optionPoolsSummary?: IOptionPoolSummary;
  warrantBlocksSummary?: IWarrantSummary;
  investmentsDetails?: IInvestmentDetail[];
  fundsSummaries?: ICaptableFundSummary[];
}

export interface ICaptableDataModel {
  captableInvestments: ICaptableInvestmentDataModel[] | null;
  cartaType?: string | null;
  cashRaised: number | null;
  companyId: number;
  createdAt: string;
  createdBy?: string;
  currencyId: number;
  date: string;
  deletedAt?: string | null;
  fullyDilutedShares: number | null;
  fundsSummaries: ICaptableFundSummary[];
  id: number;
  investmentsSummaries?: ICaptableInvestmentSummary;
  name: string | null;
  outstandingShares: number | null;
  primary: boolean | null;
  published: boolean | null;
  source: CaptableSource;
  stakeholdersSummaries?: IStakeholderSummary[];
  stakeholdersTotals?: IStakeholderTotals[] | null; // available only on GET
  type: CaptableType;
  updatedAt?: string;
  updatedBy?: string;
  uuid: string;
}

export type IStakeholderInvestmentDataModel = yup.InferType<
  ReturnType<typeof StakeholderInvestmentSchemas.create>
>;

export type ICaptableInvestmentDataModel = Omit<
  yup.InferType<ReturnType<typeof captableInvestmentFormSchema>>,
  'investmentDate'
> & {
  investmentDate?: string | Date | null;
  id: number;
};

export function createCaptableDataModel(overrides: Partial<ICaptableDataModel> = {}): ICaptableDataModel {
  return {
    captableInvestments: [],
    cashRaised: null,
    companyId: 0,
    createdAt: '',
    currencyId: 0,
    date: '',
    fullyDilutedShares: 0,
    fundsSummaries: [],
    id: -1,
    name: '',
    outstandingShares: null,
    primary: false,
    published: false,
    source: CaptableSource.none,
    type: CaptableType.system,
    uuid: '',
    ...overrides,
  };
}

export function createCaptableInvestment(
  overrides: Partial<ICaptableInvestmentDataModel> = {}
): ICaptableInvestmentDataModel {
  try {
    return {
      // cast will throw if any required fields are missing
      ...captableInvestmentFormSchema().cast({
        captableId: 0,
        currencyId: 1,
        id: 0,
        name: '',
      }),
      ...overrides,
    } as ICaptableInvestmentDataModel;
  } catch (e) {
    console.error(e);
    return {
      ...captableInvestmentFormSchema().getDefault(),
      captableId: 0,
      currencyId: 1,
      id: 0,
      name: '',
      ...overrides,
    };
  }
}

export function createStakeholderInvestment(
  overrides: Partial<IStakeholderInvestmentDataModel> = {}
): IStakeholderInvestmentDataModel {
  try {
    return {
      ...StakeholderInvestmentSchemas.create().cast({
        currencyId: 1,
        stakeholderName: '',
      }),
      ...overrides,
    };
  } catch (e) {
    console.error(e);
    return {
      ...StakeholderInvestmentSchemas.create().getDefault(),
      currencyId: 1,
      stakeholderName: '',
      ...overrides,
    };
  }
}

export interface IStakeholderTotals {
  fundId?: number;
  stakeholderName?: string;
  cashRaised?: number | null;
  fullyDilutedShares?: number | null;
  ownershipPercentage?: number | null;
}

export const StakeholderTBDName = 'Stakeholder - TBD';
